// routes
import { useEffect, useState } from 'react';
import MotionLazyContainer from './components/animate/MotionLazyContainer';
import ThemeSettings from './components/settings';
import Router from './routes';
// theme
import ThemeProvider from './theme';
import {
  DevModeBadge,
  LoadingScreen,
  ProgressBarStyle,
  ScrollToTop,
} from 'components';
import { DEV_NAMESPACE } from 'constants/paths';
import { buildSorting, useJsonApi } from 'hooks';
import { useAuth } from 'hooks/api';
import { LoadingProvider, SnackbarProvider } from 'providers';
import {
  DrupalNotificationStatusLabel,
  convertDrupalToSystemNotification,
  useAuthSelector,
  useGlobalStore,
  useNotificationsSelector,
  useNotificationsStore,
} from 'stores';
import { IDrupalNotification } from 'types/notification';
// components

export default function App() {
  const { notifications } = useNotificationsSelector();
  const { showLoader, hideLoader } = useGlobalStore();
  const { listEntities } = useJsonApi({ showLoader, hideLoader });
  const { storeSession, reset } = useAuthSelector();
  const { checkDrupalSession } = useAuth();
  const [isDevMode, setIsDevMode] = useState(false);
  const [hrefPrev, setHrefPrev] = useState('');
  const [sessionCheckDone, setSessionCheckDone] = useState(false);
  const { origin, href } = window.location;

  /**
   * All'avvio: se l'utente non è autenticato via OAuth2 (nessun token in store),
   * controlla se il browser ha un cookie di sessione Drupal attivo.
   * Se sì, imposta la sessione in modalità cookie_mode (nessuna regressione
   * per gli utenti già autenticati tramite il flusso OAuth2 normale).
   */
  useEffect(() => {
    // Controlla sempre se c'è una sessione Drupal attiva al caricamento dell'app.
    // Non saltare se già autenticato: potrebbe essere un utente diverso che
    // arriva dal backoffice Drupal con un cookie di sessione differente.
    checkDrupalSession().then(session => {
      console.log('🚀 ~ App ~ session:', session);
      if (session) {
        // Reset completo dello store prima di salvare la nuova sessione:
        // evita dati sporchi da sessioni precedenti (utente diverso, token vecchi).
        reset();
        storeSession(session, true);
      }
      setSessionCheckDone(true);
    });
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      const res = await listEntities({
        body: {
          entity: 'fla_notification_user',
          bundle: 'fla_notification_user',
          additionalParams: [
            ...buildSorting({
              sortingFieldName: 'created',
              sortingOrder: 'DESC',
            }),
            ...[['include', 'fla_notification']],
          ],
          deserializeEntities: true,
          specificResValue: [
            'id',
            'title',
            'created',
            DrupalNotificationStatusLabel,
            'fla_notification',
          ],
          disableStatusFilter: true,
        },
      });
      const drupalNotification = res
        ? (res as unknown as IDrupalNotification[])
        : [];
      useNotificationsStore.setState(prev => ({
        ...prev,
        notifications: drupalNotification
          .filter(
            n => !!n.fla_notification && !!n.fla_notification.taxonomy_term
          )
          .map(n => convertDrupalToSystemNotification(n)),
      }));
    };
    if (hrefPrev !== href) {
      setHrefPrev(href);
      fetchNotifications();
    }
  }, [href]);

  useEffect(() => {
    if (!isDevMode) {
      setIsDevMode(origin.includes(DEV_NAMESPACE));
    }
  }, [isDevMode]);

  if (!notifications) return <></>;

  // Attendi la verifica della sessione Drupal prima di renderizzare il router
  // (evita il flash della pagina di login per utenti provenienti dalla dashboard)
  if (!sessionCheckDone) return <LoadingScreen />;

  return (
    <MotionLazyContainer>
      <ThemeProvider>
        <ThemeSettings>
          {isDevMode ? <DevModeBadge /> : null}
          <SnackbarProvider>
            <ProgressBarStyle />
            <ScrollToTop />
            <LoadingProvider />
            <Router />
          </SnackbarProvider>
        </ThemeSettings>
      </ThemeProvider>
    </MotionLazyContainer>
  );
}
